// マスタースケジュールのガントチャート等、AIエージェントチャットに添付される画像は
// 高解像度（スクリーンショット等）になりがちなため、アップロード前に長辺を有界化する。
// Canvas 経由で再エンコードするため、出力は常に JPEG になる。
const MAX_DIMENSION = 2000
const JPEG_QUALITY = 0.85

export type ImageDimensions = {
  width: number
  height: number
}

/**
 * 長辺が maxDimension 以下になるよう縮小後の寸法を計算する。
 * 既に maxDimension 以下の場合は拡大せず、そのままの寸法を返す。
 */
export const computeScaledDimensions = (
  original: ImageDimensions,
  maxDimension: number = MAX_DIMENSION
): ImageDimensions => {
  const longestSide = Math.max(original.width, original.height)
  if (longestSide <= maxDimension) {
    return original
  }
  const scale = maxDimension / longestSide
  return {
    width: Math.round(original.width * scale),
    height: Math.round(original.height * scale),
  }
}

// 再エンコード後は JPEG 固定になるため、拡張子を .jpg に置き換える。
const toJpegFileName = (originalName: string): string => {
  const withoutExtension = originalName.replace(/\.[^./\\]+$/, '')
  return `${withoutExtension || 'image'}.jpg`
}

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image for resizing'))
    }
    image.src = objectUrl
  })
}

const canvasToJpegFile = (
  canvas: HTMLCanvasElement,
  fileName: string
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to encode resized image'))
          return
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      JPEG_QUALITY
    )
  })
}

/**
 * 画像ファイルを長辺 maxDimension px 以下へダウンスケールし、JPEG として再エンコードする。
 * Canvas 2D コンテキストが取得できない環境（想定外）では元ファイルをそのまま返す。
 */
export const resizeImageForUpload = async (
  file: File,
  maxDimension: number = MAX_DIMENSION
): Promise<File> => {
  const image = await loadImage(file)
  try {
    const { width, height } = computeScaledDimensions(
      { width: image.naturalWidth, height: image.naturalHeight },
      maxDimension
    )
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) {
      return file
    }
    context.drawImage(image, 0, 0, width, height)
    return await canvasToJpegFile(canvas, toJpegFileName(file.name))
  } finally {
    URL.revokeObjectURL(image.src)
  }
}
